import { apiFetch } from "./client";
import type { Page } from "./admin";

export type ProcurementContractStatus = "DRAFT" | "ACTIVE" | "FULFILLED" | "CANCELLED";

export type ProcurementContract = {
  id: string;
  companyId: string;
  companyName: string;
  referenceNumber: string;
  culture: string | null;
  quantityT: number;
  pricePerT: number | null;
  harvestYear: number;
  status: ProcurementContractStatus;
  effectiveDate: string | null;
  expiresAt: string | null;
  /**
   * Uslovi dogovora. Svaki sme da bude prazan, i to je namerno: u Bankomovom registru cena u evrima po
   * toni stoji na 71 % ugovora, rok plaćanja na 61 %, paritet na 7 %. Uslov koji nije dogovoren se
   * prikazuje prazan, ne pogađa se.
   */
  contractDate: string | null;
  priceCurrency: string | null;
  parity: string | null;
  loadingFrom: string | null;
  loadingTo: string | null;
  loadingNote: string | null;
  paymentDays: number | null;
  paymentBase: "LOADING" | "DELIVERY" | "INVOICE" | "FIXED_DATE" | null;
  paymentDueDate: string | null;
  /** Tekst iz kog su uslovi pročitani, kad su došli uvozom a ne od čoveka. */
  sourceNote: string | null;
  /**
   * Delivered-versus-contracted. Computed, never stored. All four are null together when the
   * fulfilment is not known: a draft from the register whose receipts are not linked to it.
   */
  settledT: number | null;
  inProgressT: number | null;
  remainingT: number | null;
  /** Real percentage — can exceed 100. The bar caps itself; this number does not. */
  fulfilmentPct: number | null;
  /** Contracted tonnage reached. A hint for OTKUP, never an automatic transition. */
  readyToClose: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Whether the caller may write on this contract at all — edit, move, amend,
   * attach papers. The otkup desk reads every supplier's contracts but acts
   * only on its assigned suppliers'; status rules come on top of this. */
  canAct: boolean;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

/**
 * Ugovori o otkupu (procurement contracts) — supplier-side counterpart of
 * `contractsApi`, backed by `/api/procurement/contracts`. RLS + service-layer
 * scoping mean a SUPP_MANAGER caller only ever sees their own company's rows.
 */
export type ProcurementContractDocumentKind = "CONTRACT" | "ANNEX" | "OTHER";

export type ProcurementContractDocument = {
  id: string;
  kind: ProcurementContractDocumentKind;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  createdAt: string;
};

/** Mirrors ProcurementContractDocumentService: 25 MB, 10 per contract, PDF + images. */
export const CONTRACT_DOC_MAX_BYTES = 25 * 1024 * 1024;
export const CONTRACT_DOC_MAX_FILES = 10;
export const CONTRACT_DOC_ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

export type ProcurementContractAmendment = {
  id: string;
  effectiveDate: string;
  quantityT: number | null;
  pricePerT: number | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
};

export const procurementContractsApi = {
  /**
   * Ugovori o otkupu, po stranama.
   *
   * Veličina se uvek šalje, i kad je podrazumevana: registar raste sa poslom — iz evidencije otkupa
   * ulazi 580 zaključenih ugovora — a red nosi i uslove dogovora i izračunato ispunjenje. Pozivalac
   * kome trebaju samo neki redovi (kontrolna tabla, birač ugovora na najavi) traži malu stranu i, kad
   * mu je stalo da je videlo sve, čita `totalElements`, umesto da povuče ceo registar pa ga seče u
   * pregledaču.
   */
  list: (
    params: { companyId?: string; status?: ProcurementContractStatus } = {},
    page = 0,
    size = 25,
  ) =>
    apiFetch<Page<ProcurementContract>>(
      `/procurement/contracts${q({ ...params, page: String(page), size: String(size) })}`,
    ),
  get: (id: string) => apiFetch<ProcurementContract>(`/procurement/contracts/${id}`),

  // Documents: presign -> browser PUTs straight to MinIO -> confirm. Bytes never
  // pass through the backend (same flow as complaint attachments).
  listDocuments: (id: string) =>
    apiFetch<ProcurementContractDocument[]>(`/procurement/contracts/${id}/documents`),
  presignDocument: (
    id: string,
    body: { fileName: string; contentType: string; sizeBytes: number; kind: ProcurementContractDocumentKind },
  ) => apiFetch<{ objectKey: string; url: string }>(`/procurement/contracts/${id}/documents/presign`, {
    method: "POST",
    body,
  }),
  confirmDocument: (
    id: string,
    body: {
      objectKey: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
      kind: ProcurementContractDocumentKind;
    },
  ) => apiFetch<ProcurementContractDocument>(`/procurement/contracts/${id}/documents/confirm`, {
    method: "POST",
    body,
  }),
  // Amendments: append-only history of contract terms. No update, no delete —
  // an amendment is corrected by a further amendment.
  listAmendments: (id: string) =>
    apiFetch<ProcurementContractAmendment[]>(`/procurement/contracts/${id}/amendments`),
  createAmendment: (
    id: string,
    body: {
      effectiveDate: string;
      quantityT?: number | null;
      pricePerT?: number | null;
      note?: string | null;
    },
  ) => apiFetch<ProcurementContractAmendment>(`/procurement/contracts/${id}/amendments`, {
    method: "POST",
    body,
  }),

  documentDownloadUrl: (id: string, documentId: string) =>
    apiFetch<{ url: string }>(`/procurement/contracts/${id}/documents/${documentId}/download-url`),
  deleteDocument: (id: string, documentId: string) =>
    apiFetch<void>(`/procurement/contracts/${id}/documents/${documentId}`, { method: "DELETE" }),

  create: (body: {
    companyId: string;
    referenceNumber: string;
    culture?: string;
    quantityT: number;
    pricePerT?: number;
    harvestYear: number;
    effectiveDate?: string;
    expiresAt?: string;
  }) => apiFetch<ProcurementContract>("/procurement/contracts", { method: "POST", body }),

  update: (
    id: string,
    body: Partial<
      Pick<
        ProcurementContract,
        "quantityT" | "pricePerT" | "harvestYear" | "effectiveDate" | "expiresAt"
      >
    >,
  ) => apiFetch<ProcurementContract>(`/procurement/contracts/${id}`, { method: "PUT", body }),

  transition: (id: string, to: ProcurementContractStatus) =>
    apiFetch<ProcurementContract>(`/procurement/contracts/${id}/transition`, { method: "POST", body: { to } }),
};

/** A supplier the caller may work with — the pick-list behind a contract or a delivery announcement. */
export type ProcurementSupplier = { id: string; name: string };

/**
 * `/api/procurement/suppliers` — every active supplier company the caller may
 * see, narrowed server-side exactly like the contracts and deliveries are. The
 * otkup desk holds no `companies:view`, so this — not the admin directory — is
 * where its screens take the supplier list from.
 *
 * `actable: true` narrows it to the suppliers the caller may open a contract or
 * an announcement for — the dialogs use that; the list filters use the default.
 */
export const procurementSuppliersApi = {
  list: (opts: { actable?: boolean } = {}) =>
    apiFetch<ProcurementSupplier[]>(`/procurement/suppliers${opts.actable ? "?actable=true" : ""}`),

  /**
   * Dobavljači po ukucanom tekstu, traženi u bazi.
   *
   * Postoji zato što je birač dobijao ceo spisak — 2.113 redova pri svakom otvaranju ekrana — pa ga
   * sekao na pedeset u pregledaču. Ko je tražio firmu, kucao je u spisak koji je već bio odsečen, što se
   * čita kao „te firme nema". `totalAtMost` je gornja granica broja pogodaka, da ekran može da kaže od
   * čega je odsečeno.
   */
  search: (q: string, limit = 50) =>
    apiFetch<{ rows: ProcurementSupplier[]; totalAtMost: number; cut: boolean }>(
      `/procurement/suppliers/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),
};
